import { memo, useRef } from "react";
import PropTypes from "prop-types";

import { withALErrorBoundary } from "../../helpers/ErrorBoundary/ALErrorBoundary";
import { cn } from "@helpers/general";
import ALLoading from "./ALLoading";

function ALButtonV2({
  className = "",
  size = "medium",
  variant = "primary",
  isLoading = false,
  type = "button",
  fullWidth = false,
  buttonBackgroundColor = "",
  buttonTextColor = "",
  showBorder = false,
  disabled = false,
  onClick = () => {},
  children,
  ...rest
}) {
  const buttonRef = useRef(null);

  const styleVars =
    buttonBackgroundColor && buttonTextColor
      ? { "--btn-bg": buttonBackgroundColor, "--btn-fg": buttonTextColor }
      : undefined;

  return (
    <button
      ref={buttonRef}
      style={styleVars}
      disabled={disabled}
      className={cn(
        "flex justify-center items-center rounded-[4px] overflow-hidden cursor-pointer font-bold px-[15px]",
        variant === "primary" &&
          "border border-primary bg-primary text-white hover:bg-white hover:text-primary",
        variant === "primary" &&
          "mobile:hover:border-primary mobile:hover:bg-primary mobile:hover:text-white",
        variant === "secondary" &&
          "border border-secondary bg-secondary text-white hover:bg-white hover:text-secondary",
        variant === "secondary" &&
          "mobile:hover:border-secondary mobile:hover:bg-secondary mobile:hover:text-white",
        variant === "outline" &&
          "border border-primary bg-white text-primary enabled:hover:bg-primary enabled:hover:text-white",
        variant === "outline" &&
          "mobile:hover:border-primary mobile:hover:bg-primary mobile:hover:text-white",
        variant === "tertiary" &&
          "border border-light-grey bg-light-grey text-black hover:bg-black hover:text-white",
        variant === "link" &&
          "border border-transparent bg-white text-primary hover:bg-primary hover:text-white hover:border-primary",
        variant === "link" &&
          "mobile:hover:border-primary mobile:hover:bg-primary mobile:hover:text-white",
        size === "large" && "h-[45px] typo-h3 desktop:typo-body",
        size === "medium" && "h-[35px] mobile:h-[28px] typo-body",
        size === "small" && "h-[35px] mobile:h-[24px] typo-micro desktop:typo-body",
        buttonBackgroundColor &&
          buttonTextColor &&
          `bg-[var(--btn-bg)]
          border
          ${showBorder ? "border-[var(--btn-fg)]" : "border-[var(--btn-bg)]"}
          text-[var(--btn-fg)]
          hover:bg-[var(--btn-fg)]
          hover:border-[var(--btn-bg)]
          hover:text-[var(--btn-bg)]
          mobile:hover:bg-[var(--btn-bg)]
          ${
            showBorder
              ? "mobile:hover:border-[var(--btn-fg)]"
              : "mobile:hover:border-[var(--btn-bg)]"
          }
          mobile:hover:text-[var(--btn-fg)]
          `,
        fullWidth ? "w-full" : "min-w-[250px]",
        disabled &&
          "cursor-not-allowed pointer-events-none bg-dark-grey text-white border border-dark-grey mobile:hover:bg-dark-grey mobile:hover:border-dark-grey mobile:hover:text-white",
        className
      )}
      onClick={onClick}
      type={type}
      {...rest}
    >
      {isLoading ? <ALLoading className="w-[30px]" /> : children}
    </button>
  );
}

ALButtonV2.propTypes = {
  className: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  variant: PropTypes.oneOf(["primary", "secondary", "outline", "link", "tertiary"]),
  isLoading: PropTypes.bool,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  onClick: PropTypes.func,
  fullWidth: PropTypes.bool,
  buttonBackgroundColor: PropTypes.string,
  buttonTextColor: PropTypes.string,
  showBorder: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node,
};

export default withALErrorBoundary({
  name: "ALButtonV2",
  priority: "P1",
})(memo(ALButtonV2));
